import React, { useMemo } from 'react';
import { getDayInResourceTz } from '../../../../utils/datetime';
import { EXCLUDED_HATCH_CLASS } from '../../../../utils/excludedDays';

const NO_EXCLUDED_DAYS = new Map();

const getRankTierClass = (rank) => {
  if (rank == null) return 'border border-gray-300 dark:border-gray-600 border-gray-300 dark:border-gray-600';
  if (rank <= 5) return 'bg-green-500 border-green-600 dark:bg-green-400 dark:border-green-500';
  if (rank <= 10) return 'bg-brand-500 border-brand-600 dark:bg-brand-400 dark:border-brand-500';
  return 'bg-amber-500 border-amber-600 dark:bg-amber-400 dark:border-amber-500';
};

// Active day the user didn't qualify on (penalty day): light-red "NQ" tile.
const NQ_TILE_CLASS = 'bg-red-100 border-red-300 dark:bg-red-500/25 dark:border-red-500/50';

const getErrorTierClass = (value, min, max) => {
  if (value == null) return 'border border-gray-300 dark:border-gray-600 border-gray-300 dark:border-gray-600';
  if (min === max) return 'bg-amber-500 border-amber-600 dark:bg-amber-400 dark:border-amber-500';
  const ratio = (value - min) / (max - min);
  if (ratio <= 0.25) return 'bg-green-500 border-green-600 dark:bg-green-400 dark:border-green-500';
  if (ratio <= 0.5) return 'bg-lime-500 border-lime-600 dark:bg-lime-400 dark:border-lime-500';
  if (ratio <= 0.75) return 'bg-amber-500 border-amber-600 dark:bg-amber-400 dark:border-amber-500';
  return 'bg-red-500 border-red-600 dark:bg-red-400 dark:border-red-500';
};

/**
 * One tile per day of the selected month, color-coded by rank tier or error magnitude.
 * Mirrors the D+1/D+n "Daily Ranks" section so Intraday can share the visual.
 *
 * @param {Array} data - daily_ranks_json (rankings) or daily_scores_json (errors).
 *                      Expected entry shape: { target_day|date, rank|value }
 * @param {'rankings'|'errors'} mode
 * @param {number} year
 * @param {number} month  - 1-12
 * @param {string} [monthLabel] - e.g. "April"; used in the header sentence
 * @param {Array<string>} [missedDays] - rankings-only: target_day strings for
 *        active days the user didn't qualify on (penalty days). Rendered as
 *        clickable "NQ" tiles since they have no rank but a valid date.
 * @param {Map<string, string>} [excludedDays] - ISO day -> reason of outage
 *        days left out of rankings and payments: hatched, not clickable.
 */
export default function DailyPerformanceStrip({
  data = [],
  mode = 'rankings',
  year,
  month,
  monthLabel,
  onDayClick,
  dayClickDisabledHint,
  missedDays = [],
  excludedDays = NO_EXCLUDED_DAYS,
}) {
  const isRankings = mode === 'rankings';
  const eyebrow = isRankings ? 'Daily Ranks' : 'Daily Errors';
  const headline = isRankings
    ? `Your daily ranks for ${monthLabel || ''} ${year}`.trim()
    : `Your daily errors for ${monthLabel || ''} ${year}`.trim();

  const { valueByDay, dateByDay, errorRange, daysInMonth, daysWithData, missedByDay } = useMemo(() => {
    const dim = new Date(year, month, 0).getDate();
    const byDay = {};
    const dateMap = {};
    const errs = [];

    for (const entry of data || []) {
      const dateStr = entry.target_day || entry.date;
      if (!dateStr || excludedDays.has(dateStr)) continue;
      const day = getDayInResourceTz(dateStr);
      if (day == null || day < 1 || day > dim) continue;

      const value = isRankings ? entry.rank : (entry.value ?? entry.score);
      byDay[day] = value ?? null;
      dateMap[day] = dateStr;
      if (!isRankings && value != null) errs.push(value);
    }

    // Map active-but-unqualified days to their real date so they stay
    // clickable. A ranked day always wins (it carries a rank to show).
    const missedMap = {};
    if (isRankings) {
      for (const ds of missedDays || []) {
        if (!ds || excludedDays.has(ds)) continue;
        const day = getDayInResourceTz(ds);
        if (day == null || day < 1 || day > dim) continue;
        if (byDay[day] != null) continue;
        missedMap[day] = ds;
      }
    }

    const min = errs.length ? Math.min(...errs) : 0;
    const max = errs.length ? Math.max(...errs) : 1;
    const withData = Object.values(byDay).filter((v) => v != null).length;

    return {
      valueByDay: byDay,
      dateByDay: dateMap,
      errorRange: { min, max },
      daysInMonth: dim,
      daysWithData: withData,
      missedByDay: missedMap,
    };
  }, [data, isRankings, year, month, missedDays, excludedDays]);

  // Day of month -> reason, for the excluded (hatched) tiles.
  const excludedByDay = useMemo(() => new Map(
    [...excludedDays].map(([isoDay, reason]) => [Number(isoDay.slice(8)), reason]),
  ), [excludedDays]);

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const missedCount = Object.keys(missedByDay).length;

  const tileClass = (day) => {
    if (excludedByDay.has(day)) return `border-gray-300 dark:border-gray-600 ${EXCLUDED_HATCH_CLASS}`;
    const v = valueByDay[day];
    if (isRankings) {
      if (v != null) return getRankTierClass(v);
      if (missedByDay[day]) return NQ_TILE_CLASS;
      return getRankTierClass(null);
    }
    return getErrorTierClass(v, errorRange.min, errorRange.max);
  };

  const tileTitle = (day) => {
    if (excludedByDay.has(day)) return `Day ${day}: Excluded · ${excludedByDay.get(day)}`;
    const v = valueByDay[day];
    if (v == null) {
      if (isRankings && missedByDay[day]) return `Day ${day}: Not qualified`;
      return `Day ${day}: No data`;
    }
    if (isRankings) return `Day ${day}: Rank #${v}`;
    return `Day ${day}: Error ${Number(v).toFixed(2)}`;
  };

  return (
    <section className="mb-4">
      <header className="mb-2">
        <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
          {eyebrow}
        </div>
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
          <h3 className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">
            {headline}
          </h3>
          {onDayClick && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Select a day to open its detailed view.
            </span>
          )}
        </div>
      </header>
      <div className="bg-surface dark:bg-gray-870 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-600/70 overflow-hidden">
        <div className="p-5">
          {daysWithData === 0 && missedCount === 0 ? (
            <div className="h-28 rounded-md bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 flex items-center justify-center">
              <div className="text-sm text-gray-500">No data available for selected month</div>
            </div>
          ) : (
            <div className="space-y-3">
              <div
                className="grid"
                style={{
                  gridTemplateColumns: `repeat(${daysInMonth}, minmax(10px, 1fr))`,
                  gridTemplateRows: 'auto 22px',
                  gap: '2px',
                }}
              >
                {days.map((day) => (
                  <div
                    key={`hdr-${day}`}
                    className="text-[9px] text-center font-mono tabular-nums text-gray-400 dark:text-gray-500 leading-tight pb-[2px]"
                  >
                    {day}
                  </div>
                ))}
                {days.map((day) => {
                  const isNq = isRankings && valueByDay[day] == null && missedByDay[day];
                  const dateStr = dateByDay[day] || (isNq ? missedByDay[day] : undefined);
                  const clickable = onDayClick && dateStr && !excludedByDay.has(day);
                  const Tag = clickable ? 'button' : 'div';
                  const baseTitle = tileTitle(day);
                  const tooltipSuffix = clickable
                    ? ' · click for drill-down'
                    : (dayClickDisabledHint ? ` · ${dayClickDisabledHint}` : '');
                  return (
                    <Tag
                      key={`cell-${day}`}
                      type={clickable ? 'button' : undefined}
                      onClick={clickable ? () => onDayClick(dateStr) : undefined}
                      className={`rounded-md relative group ${
                        clickable
                          ? 'cursor-pointer hover:ring-2 hover:ring-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
                          : 'cursor-default'
                      }`}
                      style={{ height: '22px', padding: 0 }}
                      title={baseTitle + tooltipSuffix}
                    >
                      {/* Colour sits on its own layer so the dark-mode toning
                          (desaturate + dim) never touches the number on top. */}
                      <span
                        aria-hidden="true"
                        className={`absolute inset-0 rounded-md border ${tileClass(day)} dark:saturate-[.65] dark:brightness-[.82]`}
                      />
                      {isRankings && valueByDay[day] != null && (
                        <span
                          className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white font-mono tabular-nums leading-none pointer-events-none"
                          style={{ textShadow: '0 1px 1px rgba(0,0,0,0.35)' }}
                        >
                          {valueByDay[day]}
                        </span>
                      )}
                      {isNq && (
                        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-red-600 dark:text-red-300 leading-none pointer-events-none">
                          NQ
                        </span>
                      )}
                      <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10 pointer-events-none">
                        {baseTitle}{tooltipSuffix}
                      </div>
                    </Tag>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 dark:[&_span]:saturate-[.65] dark:[&_span]:brightness-[.82]">
                {isRankings ? (
                  <>
                    <div className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded-sm bg-green-500 border border-green-600 inline-block" />
                      Top 5
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded-sm bg-brand-500 border border-brand-600 inline-block" />
                      Top 6–10
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded-sm bg-amber-500 border border-amber-600 inline-block" />
                      Other
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded-sm bg-red-100 border border-red-300 dark:bg-red-500/25 dark:border-red-500/50 inline-block" />
                      Not qualified
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded-sm bg-green-500 border border-green-600 inline-block" />
                      Low error
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded-sm bg-lime-500 border border-lime-600 inline-block" />
                      Below avg
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded-sm bg-amber-500 border border-amber-600 inline-block" />
                      Above avg
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="h-3 w-3 rounded-sm bg-red-500 border border-red-600 inline-block" />
                      High error
                    </div>
                  </>
                )}
                <div className="flex items-center gap-1">
                  <span className="h-3 w-3 rounded-sm border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 inline-block" />
                  No data
                </div>
                {excludedByDay.size > 0 && (
                  <div className="flex items-center gap-1">
                    <span className={`h-3 w-3 rounded-sm border border-gray-300 dark:border-gray-600 inline-block ${EXCLUDED_HATCH_CLASS}`} />
                    Excluded
                  </div>
                )}
                <span className="ml-auto text-gray-400">
                  {daysWithData} days with data
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
